import {
  collection,
  getDoc,
  setDoc,
  serverTimestamp,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
  limit as qLimit,
  startAfter,
  updateDoc,
  doc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Timestamp,
  DocumentSnapshot,
  DocumentReference,
  FieldPath,
  arrayUnion,
  arrayRemove,
  deleteField,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebaseConfig";
import { getStoredLocale } from "@/composables/useLanguage";
import { markRaw } from "vue";
import type { Module } from "vuex";
import { RoleDoc, TeamDoc, UserData } from "@/models/interfaces";

export interface UserRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  thumbnail: string;
  roles: string[] | null;
  teams: string[] | null;
  skills: { id: string; title: string } | null;
  /** Their level in the organisation listed, active in it or not. */
  usertype: number;
  isNew: boolean;
  /** The organisation listed is the one they are active in. */
  activeHere: boolean;
  organisations: Record<string, number>;
}

export interface UsersState {
  fetching: { value: boolean };
  error: string;
  loggedIn: { value: boolean | null };
  user: { data: UserData | null };
  users: { data: UserRow[] };
  total: number;
  pageIndex: number;
  pageSize: number;
  cursors: Array<QueryDocumentSnapshot<DocumentData> | null>;
  nameSearch: string;
  accountId?: string | null;
  /** Every user of `accountId`, by name: the pages are cut from this. */
  allRows: UserRow[] | null;
}

const initialState = (): UsersState => ({
  fetching: { value: false },
  error: "",
  loggedIn: { value: null },
  user: { data: null },
  users: { data: [] },
  total: 0,
  pageIndex: 0,
  pageSize: 10,
  cursors: [],
  nameSearch: "",
  accountId: null,
  allRows: null,
});

type RootState = unknown; // keep as unknown since your root store is JS

/** Copies a row changed on the page into the whole list the pages are cut from. */
function syncAllRows(state: UsersState, id: string) {
  const row = state.users.data.find((u) => u.id === id);
  if (row && state.allRows) state.allRows = state.allRows.map((u) => (u.id === id ? row : u));
}

const usersModule: Module<UsersState, RootState> = {
  namespaced: true,

  state: initialState,

  getters: {
    isFetching: (state) => !!state.fetching.value,
    loggedIn: (state) => state.loggedIn.value,
    user: (state) => state.user.data,
    rows: (state) => state.users.data,
    total: (state) => state.total,
    pageIndex: (state) => state.pageIndex,
    pageSize: (state) => state.pageSize,
    hasPrev: (state) => state.pageIndex > 0,
    hasNext: (state) => (state.pageIndex + 1) * state.pageSize < state.total,
  },

  mutations: {
    SET_FETCHING(state, v: unknown) {
      state.fetching.value = !!v;
    },
    SET_LOGGED_IN(state, value: boolean) {
      state.loggedIn.value = value;
    },
    SET_USER(state, value: UserData) {
      state.user.data = value;
      // const data = value.data();
      // if(!data) throw new Error(`User document ${doc.id} does not exist`)

      // state.user.data = {
      //     id: value.id,
      //     uid: String(data.uid ?? value.id),
      //     email: String(data.email ?? ""),
      //     name: String(data.name ?? ""),
      //     phone: String(data.name ?? ""),
      //     account: Number(data.account ?? 0),
      //     role: Number(data.role ?? 0),
      //     thumbnail: String(data.name ?? ""),
      //     createdAt: data.createdAt ?? null,
      //     lastLoginAt: data.lastLoginAt ?? null,
      // }
    },
    SET_DISPLAY_NAME(state, name: string) {
      if (state.user.data) {
        state.user.data.name = name;
      }
    },
    SET_ERROR(state, msg?: string) {
      state.error = msg || "";
    },
    SET_ROWS(state, rows: UserRow[]) {
      state.users.data = Array.isArray(rows) ? rows : [];
    },
    REMOVE_USER_ROW(state, userId: string) {
      state.users.data = state.users.data.filter((u) => u.id !== userId);
      if (state.allRows) state.allRows = state.allRows.filter((u) => u.id !== userId);
    },
    SET_ALL_ROWS(state, rows: UserRow[] | null) {
      state.allRows = rows;
    },
    PATCH_USER_ROW(state, { id, patch }: { id: string; patch: Partial<UserRow> }) {
      const apply = (u: UserRow) => (u.id === id ? { ...u, ...patch } : u);
      state.users.data = state.users.data.map(apply);
      if (state.allRows) state.allRows = state.allRows.map(apply);
    },
    SET_TOTAL(state, n: number) {
      state.total = Number(n) || 0;
    },
    SET_PAGE_INDEX(state, i: number) {
      state.pageIndex = Number(i) || 0;
    },
    SET_PAGE_SIZE(state, n: number) {
      state.pageSize = Number(n) || 10;
    },
    RESET_PAGINATION(state) {
      state.pageIndex = 0;
      state.cursors = [];
    },
    SET_CURSOR_FOR_INDEX(
      state,
      { index, snap }: { index: number; snap: QueryDocumentSnapshot | null }
    ) {
      state.cursors[index] = snap || null;
    },
    SET_NAME_SEARCH(state, term: string) {
      state.nameSearch = String(term || "").trim();
    },
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
    UPDATE_USER_ROLE(state, { id, role }: { id: string; role: number }) {
      const r = Number(role);
      state.users.data = state.users.data.map((u) =>
        u.id === id ? { ...u, role: r } : u
      );
    },
    ADD_USER_ROLE_REF_LOCAL(
      state,
      { id, refPath }: { id: string; refPath: string }
    ) {
      state.users.data = state.users.data.map((u) =>
        u.id !== id
          ? u
          : {
              ...u,
              roles: Array.from(new Set([...(u.roles || []), refPath])),
            }
      );
      if (state.user.data?.id === id) {
        const current = (state.user.data.roles ||
          []) as DocumentReference<RoleDoc>[];
        // keep state.user.data.roles as refs; we only mirror display list in rows
        // no-op here to avoid mixing ref types; rows handle display
      }
      syncAllRows(state, id);
    },
    REMOVE_USER_ROLE_REF_LOCAL(
      state,
      { id, refPath }: { id: string; refPath: string }
    ) {
      state.users.data = state.users.data.map((u) =>
        u.id !== id
          ? u
          : { ...u, roles: (u.roles || []).filter((p) => p !== refPath) }
      );
      syncAllRows(state, id);
    },
    ADD_TEAM_REF_LOCAL(
      state,
      { id, refPath }: { id: string; refPath: string }
    ) {
      state.users.data = state.users.data.map((u) =>
        u.id !== id
          ? u
          : {
              ...u,
              teams: Array.from(new Set([...(u.teams || []), refPath])),
            }
      );
      if (state.user.data?.id === id) {
        const current = (state.user.data.teams ||
          []) as DocumentReference<TeamDoc>[];
        // keep state.user.data.roles as refs; we only mirror display list in rows
        // no-op here to avoid mixing ref types; rows handle display
      }
      syncAllRows(state, id);
    },
    REMOVE_TEAM_REF_LOCAL(
      state,
      { id, refPath }: { id: string; refPath: string }
    ) {
      state.users.data = state.users.data.map((u) =>
        u.id !== id
          ? u
          : { ...u, teams: (u.teams || []).filter((p) => p !== refPath) }
      );
      syncAllRows(state, id);
    },
    SET_ACCOUNT_ID(state, accountId: string | null) {
      state.accountId = accountId ?? null;
      state.allRows = null;
    },
  },

  actions: {
    async setAccount({ commit, dispatch }, accountId: number | null) {
      commit("SET_ACCOUNT_ID", accountId);
      commit("RESET_PAGINATION");
      await (dispatch as any)("initTotal");
      return (dispatch as any)("loadPage", { index: 0 });
    },

    async fetchUser({ commit }, firebaseUser: any) {
      if (!firebaseUser) {
        commit("SET_USER", null);
        commit("SET_LOGGED_IN", false);
        return;
      }

      // Display name fallback for UI only
      commit(
        "SET_DISPLAY_NAME",
        firebaseUser.displayName || firebaseUser.email?.split("@")[0] || ""
      );

      const ref = doc(db, "users", firebaseUser.uid);

      try {
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          // Fallback: create a minimal doc if something went wrong with registration
          const newUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || "",
            name:
              firebaseUser.displayName ||
              firebaseUser.email?.split("@")[0] ||
              "",
            phone: "",
            account: 0,
            usertype: 0,
            locale: getStoredLocale(),
            createdAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
          };
          await setDoc(ref, newUser as DocumentData);
          commit("SET_USER", { id: ref.id, ...(newUser as UserData) });
        } else {
          const data = snap.data() as UserData;

          // only bump lastLoginAt now
          await updateDoc(ref, { lastLoginAt: serverTimestamp() });

          commit("SET_USER", { id: snap.id, ...data });
        }

        commit("SET_LOGGED_IN", true);
      } catch (err: any) {
        console.error(err);
        commit("SET_USER", null);
        commit("SET_LOGGED_IN", false);
        commit("SET_ERROR", err?.message || String(err));
        throw err;
      }
    },

    async initTotal({ commit, state }) {
      commit("SET_TOTAL", state.allRows?.length ?? 0);
    },

    setPageSize({ commit, dispatch }, size: number) {
      commit("SET_PAGE_SIZE", size);
      commit("RESET_PAGINATION");
      return (dispatch as any)("loadPage", { index: 0 });
    },

    setNameSearch({ commit, dispatch }, term: string) {
      commit("SET_NAME_SEARCH", term);
      commit("RESET_PAGINATION");
      return (dispatch as any)("loadPage", { index: 0 });
    },

    /**
     * One page of the organisation's users. They are all fetched once, by
     * two queries because Firestore cannot ask for either at once in name
     * order: those active in it (`account`) and those who belong to it but
     * are active in another of theirs (their `organisations` map). Each row
     * carries their level here, and only this organisation's teams and
     * roles.
     */
    async loadPage(
      { state, commit },
      { index = 0 }: { index?: number }
    ) {
      commit("SET_FETCHING", true);
      commit("SET_ERROR", "");

      try {
        if (!state.accountId) {
          commit("SET_ROWS", []);
          commit("SET_PAGE_INDEX", 0);
          commit("SET_FETCHING", false);
          return;
        }

        if (!state.allRows) {
          const account = Number(state.accountId);
          const users = collection(db, "users");
          const [active, elsewhere, teams, roles] = await Promise.all([
            getDocs(query(users, where("account", "==", account))),
            getDocs(query(users, where(new FieldPath("organisations", String(account)), ">=", 0))),
            getDocs(query(collection(db, "teams"), where("account", "==", account))),
            getDocs(query(collection(db, "roles"), where("account", "==", account))),
          ]);
          const teamPaths = new Set(teams.docs.map((d) => "/" + d.ref.path));
          const rolePaths = new Set(roles.docs.map((d) => "/" + d.ref.path));
          const byId = new Map<string, UserRow>();
          for (const d of [...active.docs, ...elsewhere.docs]) {
            if (byId.has(d.id)) continue;
            const data = (d.data() || {}) as Partial<UserData>;
            const organisations = data.organisations ?? {};
            const activeHere = Number(data.account) === account;
            byId.set(d.id, {
              id: d.id,
              name: String(data.name ?? ""),
              email: String(data.email ?? ""),
              phone: String(data.phone ?? ""),
              thumbnail: String(data.thumbnail ?? ""),
              usertype: Number((activeHere ? data.usertype : organisations[String(account)]) ?? 0),
              isNew: data.isNew === true,
              skills: data.skills ?? null,
              teams: (data.teams ?? []).map((el) => "/" + el.path).filter((p) => teamPaths.has(p)),
              roles: (data.roles ?? []).map((el) => "/" + el.path).filter((p) => rolePaths.has(p)),
              activeHere,
              organisations,
            });
          }
          commit(
            "SET_ALL_ROWS",
            [...byId.values()].sort((a, b) =>
              (a.name || a.email).localeCompare(b.name || b.email),
            ),
          );
          commit("SET_TOTAL", byId.size);
        }

        const all = state.allRows ?? [];
        commit("SET_ROWS", all.slice(index * state.pageSize, (index + 1) * state.pageSize));
        commit("SET_PAGE_INDEX", index);
      } catch (e: any) {
        commit("SET_ERROR", e?.message || String(e));
      } finally {
        commit("SET_FETCHING", false);
      }
    },

    async nextPage({ getters, dispatch, state }) {
      if (!getters.hasNext) return;
      return (dispatch as any)("loadPage", { index: state.pageIndex + 1 });
    },

    async prevPage({ getters, dispatch, state }) {
      if (!getters.hasPrev) return;
      return (dispatch as any)("loadPage", { index: state.pageIndex - 1 });
    },

    async updateUserType(
      { commit, state },
      { userId, value }: { userId: string; value: number | string }
    ) {
      commit("SET_ERROR", "");

      const coerceRole = (r: number | string): number | null => {
        if (typeof r === "number") return r;
        if (typeof r === "string" && !isNaN(Number(r))) return Number(r);
        return null;
      };

      const newUserType = coerceRole(value);
      if (newUserType === null) {
        commit("SET_ERROR", "Invalid role value.");
        return;
      }

      // The level is `usertype` while this is their active organisation,
      // and this organisation's entry in their map, which the rules keep in
      // step with it.
      const prev = state.users.data.find((u) => u.id === userId);
      const account = String(state.accountId ?? "");
      const activeHere = prev?.activeHere ?? true;
      const inMap = !!prev && account in prev.organisations;
      const args: unknown[] = [];
      if (activeHere) args.push("usertype", newUserType);
      if (!activeHere || inMap) args.push(new FieldPath("organisations", account), newUserType);
      if (prev) commit("PATCH_USER_ROW", { id: userId, patch: { usertype: newUserType } });

      try {
        const ref = doc(db, "users", userId);
        await (updateDoc as (...a: unknown[]) => Promise<void>)(ref, ...args);
      } catch (e: any) {
        if (prev) commit("PATCH_USER_ROW", { id: userId, patch: { usertype: prev.usertype } });
        commit("SET_ERROR", e?.message || String(e));
        throw e;
      }
    },

    async addUserRoleRef(
      { commit },
      { userId, roleId }: { userId: string; roleId: string }
    ) {
      if (!userId || !roleId) return;

      const userRef = doc(db, "users", userId);
      const roleRef = doc(db, "roles", roleId) as DocumentReference<RoleDoc>;
      const refPath = "/" + roleRef.path;

      // optimistic update (display list uses paths)
      commit("ADD_USER_ROLE_REF_LOCAL", { id: userId, refPath });
      try {
        await updateDoc(userRef, { roles: arrayUnion(roleRef) });
      } catch (e) {
        // rollback
        commit("REMOVE_USER_ROLE_REF_LOCAL", { id: userId, refPath });
        throw e;
      }
    },

    async removeUserRoleRef(
      { commit },
      { userId, roleId }: { userId: string; roleId: string }
    ) {
      if (!userId || !roleId) return;

      const userRef = doc(db, "users", userId);
      const roleRef = doc(db, "roles", roleId) as DocumentReference<RoleDoc>;
      const refPath = "/" + roleRef.path;

      // optimistic update
      commit("REMOVE_USER_ROLE_REF_LOCAL", { id: userId, refPath });
      try {
        await updateDoc(userRef, { roles: arrayRemove(roleRef) });
      } catch (e) {
        // rollback
        commit("ADD_USER_ROLE_REF_LOCAL", { id: userId, refPath });
        throw e;
      }
    },

    async addUserToTeamRef(
      { commit },
      { userId, teamId }: { userId: string; teamId: string }
    ) {
      if (!userId || !teamId) return;

      const userRef = doc(db, "users", userId);
      const teamRef = doc(db, "teams", teamId) as DocumentReference<TeamDoc>;
      const refPath = "/" + teamRef.path;

      // optimistic update (display list uses paths)
      commit("ADD_TEAM_REF_LOCAL", { id: userId, refPath });
      try {
        await updateDoc(userRef, { teams: arrayUnion(teamRef) });
      } catch (e) {
        commit("REMOVE_TEAM_REF_LOCAL", { id: userId, refPath });
        throw e;
      }
    },

    async removeUserFromTeamRef(
      { commit },
      { userId, teamId }: { userId: string; teamId: string }
    ) {
      if (!userId || !teamId) return;

      const userRef = doc(db, "users", userId);
      const teamRef = doc(db, "teams", teamId) as DocumentReference<TeamDoc>;
      const refPath = "/" + teamRef.path;

      commit("REMOVE_TEAM_REF_LOCAL", { id: userId, refPath });
      try {
        await updateDoc(userRef, { teams: arrayRemove(teamRef) });
      } catch (e) {
        commit("ADD_TEAM_REF_LOCAL", { id: userId, refPath });
        throw e;
      }
    },

    async fetchAll({ state, commit }) {
      if (!state.accountId) {
        console.warn("users.fetchAll() has no accountId");
        commit("SET_ROWS", []);
        return;
      }

      commit("SET_FETCHING", true);
      try {
        const qRef = query(
          collection(db, "users"),
          where("account", "==", state.accountId),
          orderBy("name")
        );
        const snap = await getDocs(qRef);

        const rows: UserRow[] = snap.docs.map((d) => {
          const data = (d.data() || {}) as Partial<UserData>;
          return {
            id: d.id,
            name: String(data.name ?? ""),
            email: String(data.email ?? ""),
            phone: String(data.phone ?? ""),
            thumbnail: String(data.thumbnail ?? ""),
            usertype: Number(data.usertype ?? 0),
            activeHere: true,
            organisations: data.organisations ?? {},
            isNew: data.isNew === true,
            skills: data.skills ?? null,
            teams: data.teams?.length
              ? data.teams.map((el) => "/" + el.path)
              : [],
            roles: data.roles?.length
              ? data.roles.map((el) => "/" + el.path)
              : [],
          };
        });

        commit("SET_ROWS", rows);
      } catch (err) {
        console.error("Error fetching users:", err);
        commit("SET_ROWS", []);
      } finally {
        commit("SET_FETCHING", false);
      }
    },

    /**
     * Deletes the user and their sign-in through the `deleteUserById`
     * callable, or, when they belong to other organisations too, only takes
     * them out of this one: its entry in their map, its teams and roles, and,
     * if this was their active organisation, another of theirs made active
     * at its level.
     */
    async deleteUserById({ commit, state }, { userId }: { userId: string }) {
      if (!userId) return;

      commit("SET_ERROR", "");

      const row = (state.allRows ?? state.users.data).find((u) => u.id === userId);
      const account = String(state.accountId ?? "");
      const others = Object.keys(row?.organisations ?? {}).filter(
        (key) => key !== account && Number(key) > 0,
      );
      const prevRows = [...state.users.data];
      const prevAll = state.allRows ? [...state.allRows] : null;
      const prevTotal = state.total;

      commit("REMOVE_USER_ROW", userId);
      commit("SET_TOTAL", Math.max(0, prevTotal - 1));

      try {
        if (row && (others.length || !row.activeHere)) {
          const args: unknown[] = [new FieldPath("organisations", account), deleteField()];
          const refs = (paths: string[] | null) => (paths ?? []).map((p) => doc(db, p.replace(/^\//, "")));
          if (row.teams?.length) args.push("teams", arrayRemove(...refs(row.teams)));
          if (row.roles?.length) args.push("roles", arrayRemove(...refs(row.roles)));
          if (row.activeHere) {
            const next = others.map(Number).sort((a, b) => a - b)[0];
            args.push("account", next, "usertype", Number(row.organisations[String(next)] ?? 0));
          }
          await (updateDoc as (...a: unknown[]) => Promise<void>)(doc(db, "users", userId), ...args);
          return { ok: true };
        }
        const callDelete = httpsCallable(functions, "deleteUserById");
        const result = await callDelete({ uid: userId });
        return result.data;
      } catch (e: any) {
        commit("SET_ROWS", prevRows);
        commit("SET_ALL_ROWS", prevAll);
        commit("SET_TOTAL", prevTotal);
        commit("SET_ERROR", e?.message || String(e));
        throw e;
      }
    },

    reset({ commit }) {
      commit("RESET_STATE");
    },
  },
};

export default usersModule;
